import React, { useState, useEffect } from "react";
import CommonTable from "../../../common/table/ComonTable";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import { primaryColor } from "../../../../utils/comon";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCreditCard, faShieldAlt, faQrcode, faUpload, faDownload } from "@fortawesome/free-solid-svg-icons";
import { uploadImageToAzure } from '../../../../services/AzureStorageService';
import { generateInvoicePDF } from "../../../../utils/generateInvoicePDF";
import { useSelector } from "react-redux";

const MyInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 10, totalRecords: 0 });
    const { user: authUser } = useSelector((state) => state.auth);
    const unitId = authUser?.branchorunitID || 1;

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isSimulatingPayment, setIsSimulatingPayment] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const res = await apiServices({
                method: "GET",
                url: `/maintenance/unit/${unitId}`,
                useNodeApi: true
            });
            if (res?.success && res?.data?.data) {
                const fetchedList = Array.isArray(res.data.data) ? res.data.data : [];
                setInvoices(fetchedList);
                setPagination(prev => ({ ...prev, totalRecords: fetchedList.length }));
            }
        } catch (error) {
            console.error("Failed to fetch unit invoices");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchInvoices();
    }, [unitId]);

    const handlePay = (invoice) => {
        setSelectedInvoice(invoice);
        setPaymentMethod(null);
        setReceiptFile(null);
    };

    const handleConfirmPayment = async () => {
        setIsSimulatingPayment(true);
        try {
            // Call backend to create order
            const orderRes = await apiServices({
                method: "POST",
                url: "/payment/create-order",
                data: { amount: selectedInvoice.TotalAmount, invoiceId: selectedInvoice.InvoiceId },
                useNodeApi: true
            });

            const data = orderRes?.data;

            if (!orderRes?.success || !data?.order) {
                console.error("Order Creation Failed:", orderRes);
                toast.error(`Failed: ${orderRes?.message || 'Check if Node backend is running/restarted'}`);
                setIsSimulatingPayment(false);
                return;
            }

            // Real test flow: load script
            const resScript = await loadRazorpayScript();
            if (!resScript) {
                toast.error("Razorpay SDK failed to load. Are you online?");
                setIsSimulatingPayment(false);
                return;
            }

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_O00713QxO3nCkr', // Public dummy key for testing
                amount: data.order.amount,
                currency: "INR",
                name: "ZentComm Maintenance",
                description: `Invoice #${selectedInvoice.InvoiceNumber}`,
                // Only pass order_id if it's NOT a mock flow, to allow client-side test checkout
                ...(data.mock ? {} : { order_id: data.order.id }),
                handler: async function (response) {
                    try {
                        const verifyRes = await apiServices({
                            method: "POST",
                            url: "/payment/verify",
                            data: {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                invoiceId: selectedInvoice.InvoiceId,
                                mock: data.mock
                            },
                            useNodeApi: true
                        });

                        if (verifyRes?.success) {
                            toast.success("Payment successful! Invoice marked as PAID.");
                            fetchInvoices();
                        } else {
                            toast.error("Payment verification failed");
                        }
                    } catch (err) {
                        toast.error("Payment verification failed");
                    } finally {
                        setIsSimulatingPayment(false);
                        setSelectedInvoice(null);
                    }
                },
                theme: { color: primaryColor() }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response) {
                toast.error("Payment failed! Reason: " + response.error.description);
                setIsSimulatingPayment(false);
            });
            rzp.open();
        } catch (error) {
            toast.error("Error initiating payment.");
            setIsSimulatingPayment(false);
        }
    };

    const handleReceiptSubmit = async () => {
        if (!receiptFile) {
            toast.error("Please select a receipt image first.");
            return;
        }

        setIsUploadingReceipt(true);
        try {
            const receiptUrl = await uploadImageToAzure(receiptFile);

            const res = await apiServices({
                method: "PUT",
                url: `/maintenance/invoice/${selectedInvoice.InvoiceId}/submit-receipt`,
                data: { receiptUrl },
                useNodeApi: true
            });

            if (res?.success) {
                toast.success("Receipt uploaded successfully! Admin will verify it.");
                fetchInvoices();
                setSelectedInvoice(null);
                setPaymentMethod(null);
                setReceiptFile(null);
            } else {
                toast.error("Failed to submit receipt.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error uploading receipt.");
        } finally {
            setIsUploadingReceipt(false);
        }
    };

    const tableHeaders = [
        { label: "INVOICE NO", key: "invoiceNo", align: "left" },
        { label: "CATEGORY", key: "category" },
        { label: "TOTAL AMOUNT", key: "amount" },
        { label: "DUE DATE", key: "dueDate" },
        { label: "STATUS", key: "status" },
        { label: "ACTION", key: "payAction" },
        { label: "DOWNLOAD", key: "downloadAction" }
    ];

    const getStatusStyles = (status) => {
        const normalizedStatus = (status || "").toUpperCase();
        switch (normalizedStatus) {
            case "PAID": return "bg-[#DCFCE7] text-[#166534]";
            case "EXPIRED": return "bg-[#FEE2E2] text-[#991B1B]";
            case "CARRIED_FORWARD": return "bg-[#F3F4F6] text-[#374151]";
            case "PENDING": return "bg-[#FEF9C3] text-[#D97706]";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const renderTableCell = (key, row) => {
        switch (key) {
            case "invoiceNo": return <span className="font-bold text-xs text-[#191C1E] dark:text-textprimary">{row?.InvoiceNumber || "—"}</span>;
            case "category": return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{row?.Category || "—"}</span>;
            case "amount": return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">₹{row?.TotalAmount || "0"}</span>;
            case "dueDate":
                const d = row?.DueDate ? new Date(row.DueDate).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—";
                return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{d}</span>;
            case "status":
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black tracking-wider uppercase ${getStatusStyles(row?.Status)}`}>
                        {row?.Status || "—"}
                    </span>
                );
            case "payAction":
                const val = (row?.Status || "").toUpperCase();
                if (val === 'PENDING') {
                    return (
                        <button
                            onClick={() => handlePay(row)}
                            className="px-3 py-1.5 text-white rounded-lg text-xs font-bold tracking-wider shadow-sm hover:shadow-md transition-all"
                            style={{ backgroundColor: primaryColor() }}
                        >
                            PAY NOW
                        </button>
                    );
                }
                if (val === 'EXPIRED') {
                    return <span className="text-xs font-bold text-gray-400 dark:text-textsecondary">EXPIRED</span>;
                }
                if (val === 'CARRIED_FORWARD') {
                    return <span className="text-xs font-bold text-gray-400 dark:text-textsecondary">TRANSFERRED</span>;
                }
                return <span className="text-xs font-bold text-[#166534] dark:text-[#22c55e]">COMPLETED</span>;
            case "downloadAction":
                if (row.items && row.items.length > 0) {
                    return (
                        <button
                            onClick={() => generateInvoicePDF(row, row.items, {})}
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-cardbg text-gray-600 dark:text-textsecondary rounded-full hover:bg-gray-200 dark:hover:bg-actionitembg transition-colors tooltip"
                            title="Download Invoice PDF"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                    );
                }
                return null;
            default: return "—";
        }
    };

    const expandedRowRender = (row) => {
        if (!row.items || row.items.length === 0) {
            return <div className="p-4 text-xs text-gray-500 dark:text-textsecondary">No invoice items found.</div>;
        }

        return (
            <div className="p-4 bg-gray-50/50 dark:bg-actionitembg rounded-lg m-2 border border-gray-100 dark:border-bordercolor">
                <h4 className="text-xs font-bold text-gray-700 dark:text-textprimary mb-3 uppercase tracking-wider">Maintenance Breakup Amount + GST</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                        <thead className="text-gray-500 dark:text-textsecondary border-b border-gray-200 dark:border-bordercolor">
                            <tr>
                                <th className="pb-2 font-semibold">DESCRIPTION</th>
                                <th className="pb-2 font-semibold">BASE AMOUNT</th>
                                <th className="pb-2 font-semibold">GST %</th>
                                <th className="pb-2 font-semibold">GST AMOUNT</th>
                                <th className="pb-2 font-semibold text-right">SUB TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {row.items.map((item, idx) => (
                                <tr key={idx} className="border-b border-gray-100 dark:border-bordercolor last:border-0">
                                    <td className="py-3 font-medium text-gray-800 dark:text-textprimary">{item.ItemType?.replace('_', ' ') || "—"} - {item.Description}</td>
                                    <td className="py-3 text-gray-600 dark:text-textsecondary">₹{item.BaseAmount || "0"}</td>
                                    <td className="py-3 text-gray-600 dark:text-textsecondary">{item.TaxPercentage || "0"}%</td>
                                    <td className="py-3 text-gray-600 dark:text-textsecondary">₹{item.TaxAmount || "0"}</td>
                                    <td className="py-3 font-bold text-gray-800 dark:text-textprimary text-right">₹{item.TotalAmount || "0"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const paginatedData = invoices.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize).map((item, idx) => ({ ...item, pageIndex: idx }));

    return (
        <div className="flex-1 flex flex-col shrink-0">
            <CommonTable
                loading={loading}
                tableHeading="My Maintenance Invoices"
                headers={tableHeaders}
                data={paginatedData}
                renderCell={(key, row) => renderTableCell(key, row)}
                expandedRowRender={expandedRowRender}
                pagination={{ ...pagination, totalRecords: invoices.length }}
                onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
                onLimitChange={(limit) => setPagination(prev => ({ ...prev, pageSize: limit, pageNumber: 1 }))}
                hideScrollbar={false}
                isLoading={loading}
            />

            {selectedInvoice && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-cardbg rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200 border border-gray-100 dark:border-bordercolor">
                        {isSimulatingPayment ? (
                            <div className="p-8 flex flex-col items-center justify-center min-h-[320px] bg-gradient-to-b from-white to-gray-50 dark:from-cardbg dark:to-actionitembg">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-5xl text-blue-500 mb-6" />
                                <h3 className="font-bold text-gray-800 dark:text-textprimary text-lg mb-2">Processing Payment...</h3>
                                <p className="text-xs text-gray-500 dark:text-textsecondary text-center px-4">
                                    Please do not close this window or press the back button. We are securely connecting to the dummy payment gateway.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="p-4 border-b border-gray-100 dark:border-bordercolor flex justify-between items-center bg-gray-50/80 dark:bg-actionitembg">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
                                            <FontAwesomeIcon icon={faCreditCard} className="text-sm" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-800 dark:text-textprimary text-sm">Checkout</h3>
                                            <p className="text-[10px] text-gray-500 dark:text-textsecondary">Invoice #{selectedInvoice.InvoiceNumber}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedInvoice(null)} className="text-gray-400 dark:text-textsecondary hover:text-gray-600 dark:hover:text-textprimary text-xl font-bold leading-none p-1">&times;</button>
                                </div>

                                <div className="p-6 flex flex-col gap-4">

                                    {!paymentMethod ? (
                                        <div className="flex flex-col gap-3 mt-2">
                                            <p className="text-xs font-bold text-gray-600 dark:text-textprimary mb-1">Select Payment Method:</p>
                                            <button
                                                onClick={() => setPaymentMethod('RAZORPAY')}
                                                className="flex items-center gap-3 p-4 border border-[#E2E8F0] dark:border-bordercolor rounded-xl hover:bg-gray-50 dark:hover:bg-actionitembg hover:border-blue-300 transition-all text-left bg-white dark:bg-cardbg"
                                            >
                                                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-300 flex items-center justify-center shrink-0">
                                                    <FontAwesomeIcon icon={faCreditCard} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-800 dark:text-textprimary text-sm">Razorpay</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-textsecondary">Pay securely via UPI, Cards, or Netbanking</span>
                                                </div>
                                            </button>

                                            <button
                                                onClick={() => setPaymentMethod('QR_CODE')}
                                                className="flex items-center gap-3 p-4 border border-[#E2E8F0] dark:border-bordercolor rounded-xl hover:bg-gray-50 dark:hover:bg-actionitembg hover:border-blue-300 transition-all text-left bg-white dark:bg-cardbg"
                                            >
                                                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center shrink-0">
                                                    <FontAwesomeIcon icon={faQrcode} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-800 dark:text-textprimary text-sm">Scan QR Code</span>
                                                    <span className="text-[10px] text-gray-500 dark:text-textsecondary">Scan society QR code to make payment directly</span>
                                                </div>
                                            </button>
                                        </div>
                                    ) : paymentMethod === 'QR_CODE' ? (
                                        <div className="flex flex-col items-center gap-4 mt-2">
                                            {selectedInvoice.QrCodeUrl ? (
                                                <div className="p-2 border border-gray-200 dark:border-bordercolor rounded-xl bg-white dark:bg-actionitembg shadow-sm">
                                                    <img src={selectedInvoice.QrCodeUrl.startsWith('http') ? selectedInvoice.QrCodeUrl : `http://localhost:8080/${selectedInvoice.QrCodeUrl.replace(/\\/g, '/')}`} alt="Society QR Code" className="w-48 h-48 object-contain rounded-lg" />
                                                </div>
                                            ) : (
                                                <div className="w-48 h-48 bg-gray-100 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl flex flex-col items-center justify-center text-gray-400 dark:text-textsecondary p-4 text-center">
                                                    <FontAwesomeIcon icon={faQrcode} className="text-4xl mb-2 opacity-50" />
                                                    <span className="text-[10px]">No QR code provided for this maintenance</span>
                                                </div>
                                            )}
                                            <div className="text-center w-full">
                                                <p className="text-xs text-gray-600 dark:text-textsecondary font-medium mb-3">Scan using any UPI app to pay <strong className="text-gray-900 dark:text-textprimary">₹{selectedInvoice.TotalAmount}</strong></p>

                                                <div className="border border-dashed border-gray-300 dark:border-bordercolor rounded-lg p-3 bg-gray-50 dark:bg-actionitembg flex flex-col items-center justify-center relative hover:bg-gray-100 dark:hover:bg-cardbg transition-colors">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                        onChange={(e) => setReceiptFile(e.target.files[0])}
                                                    />
                                                    {receiptFile ? (
                                                        <div className="flex flex-col items-center text-emerald-600 dark:text-emerald-400">
                                                            <span className="text-xs font-bold truncate max-w-[200px]">{receiptFile.name}</span>
                                                            <span className="text-[10px] text-gray-500 dark:text-textsecondary mt-1">Tap to change file</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center text-gray-500 dark:text-textsecondary">
                                                            <FontAwesomeIcon icon={faUpload} className="text-lg mb-1" />
                                                            <span className="text-xs font-medium text-gray-700 dark:text-textprimary">Upload Receipt</span>
                                                            <span className="text-[10px]">Image only (Max 5MB)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-2 mt-2">
                                                <div className="flex justify-between text-xs border-b border-dashed border-gray-200 dark:border-bordercolor pb-2">
                                                    <span className="text-gray-500 dark:text-textsecondary">Category</span>
                                                    <span className="font-bold text-gray-800 dark:text-textprimary">{selectedInvoice.Category || 'Maintenance'}</span>
                                                </div>
                                                <div className="flex justify-between text-xs border-b border-dashed border-gray-200 dark:border-bordercolor pb-2">
                                                    <span className="text-gray-500 dark:text-textsecondary">Due Date</span>
                                                    <span className="font-bold text-gray-800 dark:text-textprimary">
                                                        {selectedInvoice.DueDate ? new Date(selectedInvoice.DueDate).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "N/A"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 justify-center mt-2 text-[10px] text-gray-400 dark:text-textsecondary font-medium bg-gray-50 dark:bg-actionitembg py-2 rounded-lg">
                                                <FontAwesomeIcon icon={faShieldAlt} className="text-emerald-500" />
                                                Secure Dummy Payment Environment
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="p-4 border-t border-gray-100 dark:border-bordercolor bg-gray-50 dark:bg-actionitembg flex justify-between gap-3 items-center">
                                    <button
                                        onClick={() => {
                                            if (paymentMethod) setPaymentMethod(null);
                                            else setSelectedInvoice(null);
                                        }}
                                        className="px-4 py-2.5 rounded-lg text-xs font-bold text-gray-600 dark:text-textsecondary bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor hover:bg-gray-50 dark:hover:bg-actionitembg hover:border-gray-300 dark:hover:border-gray-600 transition-all shadow-sm flex-1"
                                    >
                                        {paymentMethod ? 'Back' : 'Cancel'}
                                    </button>

                                    {paymentMethod === 'RAZORPAY' && (
                                        <button
                                            onClick={handleConfirmPayment}
                                            className="px-4 py-2.5 rounded-lg text-xs font-bold text-white transition-all shadow-md hover:shadow-lg active:scale-95 flex-1 flex items-center justify-center gap-2"
                                            style={{ backgroundColor: primaryColor() }}
                                        >
                                            Pay ₹{selectedInvoice.TotalAmount}
                                        </button>
                                    )}

                                    {paymentMethod === 'QR_CODE' && (
                                        <button
                                            onClick={handleReceiptSubmit}
                                            disabled={isUploadingReceipt}
                                            className={`px-4 py-2.5 rounded-lg text-xs font-bold text-white transition-all shadow-md flex-1 flex items-center justify-center gap-2 ${isUploadingReceipt ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 hover:shadow-lg active:scale-95'}`}
                                        >
                                            {isUploadingReceipt ? (
                                                <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Uploading...</>
                                            ) : (
                                                `I've Paid ₹${selectedInvoice.TotalAmount}`
                                            )}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyInvoices;
